import React, { useState, useEffect, useContext } from 'react';
import { CheckCircleIcon } from '@heroicons/react/solid';
// import { CheckCircleIcon, DocumentTextIcon } from '@heroicons/react/solid';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as Progress from '@radix-ui/react-progress';
import { functions } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { PatientDataContext } from '../../../../context/PatientDataContext';
// import { TriggerFetchLatestDocForDownload } from '../../../../utils/TriggerFetchLatestDocForDownload';
import { serverTimestamp } from 'firebase/firestore';

export default function RatingScalesWizard({ open, onOpenChange, patientId, clinicianId }) {    

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showProgressDialog, setShowProgressDialog] = useState(false);
    const [progressValues, setProgressValues] = useState([0, 0, 0]);
    const [currentStep, setCurrentStep] = useState(0);
    // const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(null);
    const { patientDataModel, updatePatientFirestore } = useContext(PatientDataContext);
    
    // Get scales from patient data
    const [availableScales, setAvailableScales] = useState([]);


    useEffect(() => {
        if (patientDataModel?.formData) {
            const scales = [];
            // Loop through formData keys to find scale results
            Object.keys(patientDataModel.formData).forEach(key => {
                const form = patientDataModel.formData[key];
                if (form?.scaleResults?.scale) {
                    scales.push({
                        id: key,
                        name: form.scaleResults.scale
                    });
                }
            });
            setAvailableScales(scales);
        }
    }, [patientDataModel]);

    const handleGenerateSection = async () => {
        try {
            setIsSubmitting(true);
            
            // Extract form IDs from availableScales
            const formIds = availableScales.map(scale => scale.id);
                    
            // Prepare data for cloud function with included forms
            const data = {
                patientId,
                clinicianId,
                includedForms: formIds
            };    

            console.log('Sending data to cloud function:', data);

            // Show initial progress dialog to indicate we're preparing
            setProgressValues([0, 0, 0]);
            setCurrentStep(0);
            setShowProgressDialog(true);

            // STEP 1: Update Firestore to set processing status
            console.log("Setting Rating Scales report processing status to 'processing'");
            await updatePatientFirestore(patientId, {
                '6_manageReports': {
                    ratingScalesReportProcessingStatus: 'processing',
                    ratingScalesReportProcessingStartedAt: serverTimestamp()
                }
            });

            // STEP 2: Trigger the cloud function (without awaiting completion)
            const generateRatingScalesReport = httpsCallable(functions, 'generateRatingScalesReport');
            
            // Show a quick progress animation before closing - just for UX purposes
            await simulateInitialProgress();
            
            // STEP 3: Close the wizard - the user doesn't need to wait
            setTimeout(() => {
                setShowProgressDialog(false);
                onOpenChange(false);
            }, 1500);

            // Fire the function without awaiting its completion
            generateRatingScalesReport(data)
                .then((result) => {
                    console.log('Cloud function completed successfully:', result.data);
                })
                .catch((error) => {
                    console.error('Cloud function failed:', error);
                    // Update Firestore with error status if the cloud function explicitly fails
                    // This is a fallback - in most cases the cloud function itself will set the status
                    updatePatientFirestore(patientId, {
                        '6_manageReports': {
                            ratingScalesReportProcessingStatus: 'error'
                        }
                    }).catch(err => {
                        console.error('Failed to update error status:', err);
                    });
                });

        } catch (error) {
            console.error('Error initiating Rating Scales generation:', error);
            setDownloadError(error.message || 'An unexpected error occurred');
            
            // Update Firestore with error status
            try {
                await updatePatientFirestore(patientId, {
                    '6_manageReports': {
                        ratingScalesReportProcessingStatus: 'error'
                    }
                });
            } catch (err) {
                console.error('Failed to update error status:', err);
            }
            
            // Keep error dialog open for user to see
            setProgressValues([100, 100, 100]);
        } finally {
            setIsSubmitting(false);
        }
    };

    /**
     * Show a brief progress animation before closing
     * This is just for UX purposes to show the user something is happening
     */
    const simulateInitialProgress = async () => {
        // Quick first step progress - just for UX purposes
        return new Promise(resolve => {
            setTimeout(() => setProgressValues([30, 0, 0]), 400);
            setTimeout(() => setProgressValues([60, 0, 0]), 800);
            setTimeout(() => {
                setProgressValues([100, 20, 0]);
                resolve();
            }, 1200);
        });
    };

    // const handleCloseDialog = () => {
    //     setShowProgressDialog(false);
    //     onOpenChange(false);
    // };

    // const handleDownload = async () => {
    //     try {
    //         setIsDownloading(true);
    //         setDownloadError(null);

    //         // Use the existing utility to trigger the download
    //         const result = await TriggerFetchLatestDocForDownload({
    //             reportStage: 'Rating Scales', // This is the name used in the cloud function
    //             clinicianId,
    //             patientId
    //         });

    //         if (!result.success) {
    //             console.error('Download failed:', result.error);
    //             setDownloadError(result.error || 'Failed to download document');
    //         } else {
    //             console.log('Download initiated successfully');
    //             // Optional: close the dialog after successful download
    //             // setTimeout(() => handleCloseDialog(), 1000);
    //         }
    //     } catch (error) {
    //         console.error('Error initiating download:', error);
    //         setDownloadError(error.message || 'An unexpected error occurred');
    //     } finally {
    //         setIsDownloading(false);
    //     }
    // };

    // Check if all progress steps are complete
    // const isProgressComplete = progressValues.every(value => value === 100);

    return (
        <>
            <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
                <AlertDialog.Portal>
                    <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 z-50" />
                    <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[85vh] w-[90vw] max-w-[550px] bg-white rounded-xl shadow-lg p-6 z-50 overflow-hidden flex flex-col">
                        <AlertDialog.Title className="text-xl font-semibold text-gray-900 mb-2">
                            Generate Rating Scales Section
                        </AlertDialog.Title>
                        <AlertDialog.Description className="text-gray-600 mb-4">
                            The following rating scales will be included in the report:
                        </AlertDialog.Description>

                        <div className="flex-grow overflow-y-auto p-0.5 mb-4" style={{ maxHeight: '50vh' }}>
                            <div className="bg-green-50 rounded-lg p-4">
                                <h4 className="text-sm font-medium text-green-800 mb-3">
                                    Included Rating Scales:
                                </h4>
                                <div className="space-y-2">
                                    {availableScales.length > 0 ? (
                                        availableScales.map((scale, index) => (
                                            <div key={index} className="flex items-center text-sm text-green-700">
                                                <CheckCircleIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                                                {scale.name}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-amber-700 italic">
                                            No rating scales found in patient data.
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                                <p className="text-sm text-blue-800">
                                    This section will process the rating scales completed by the patient and generate a 
                                    comprehensive analysis for inclusion in the assessment report.
                                </p>
                            </div>
                        </div>

                        <div className="font-semibold flex justify-end gap-3 mt-auto pt-2 border-t">
                            <AlertDialog.Cancel className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
                                Cancel
                            </AlertDialog.Cancel>
                            <AlertDialog.Action
                                onClick={handleGenerateSection}
                                disabled={isSubmitting || availableScales.length === 0}
                                className={`px-4 py-2 bg-violet-600 text-white rounded-lg transition-colors ${
                                    isSubmitting || availableScales.length === 0
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'hover:bg-violet-700'
                                }`}
                            >
                                {isSubmitting ? 'Generating...' : 'Generate Rating Scales Section'}
                            </AlertDialog.Action>
                        </div>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>

            {/* Progress Dialog - Shows briefly before closing */}
            <AlertDialog.Root open={showProgressDialog} onOpenChange={setShowProgressDialog}>
                <AlertDialog.Portal>
                    <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 z-50" />
                    <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 w-[400px] z-50">
                        <AlertDialog.Title className="text-lg font-semibold mb-4">
                            Generating Rating Scales Section
                        </AlertDialog.Title>

                        <AlertDialog.Description className="text-sm text-gray-600 mb-4">
                            Please wait while the Rating Scales Section is prepared.
                        </AlertDialog.Description>

                        <div className="space-y-6">
                            {/* Step 1 */}
                            <div className="w-full">
                                <div className="flex justify-between mb-2">
                                    <span className="text-sm">Processing Rating Scales Data...</span>
                                    {currentStep > 0 && <span className="text-green-500 text-xs">✔</span>}
                                </div>
                                <Progress.Root
                                    className="h-2 w-full bg-gray-200 rounded-full overflow-hidden"
                                    value={progressValues[0]}
                                >
                                    <Progress.Indicator
                                        className={`h-full rounded-full transition-transform duration-[300ms] ease-in-out ${
                                            progressValues[0] === 100 ? 'bg-green-600' : 'bg-violet-600'
                                        }`}
                                        style={{ transform: `translateX(-${100 - progressValues[0]}%)` }}
                                    />
                                </Progress.Root>
                            </div>

                            {/* Error message if initialization fails */}
                            {downloadError && (
                                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded relative mt-4" role="alert">
                                    <strong className="font-bold">Error: </strong>
                                    <span className="block sm:inline">{downloadError}</span>
                                </div>
                            )}
                        </div>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>
        </>
    );
}